import { VariantUpdatedEvent, VariantSelectedEvent } from '@theme/events';
// import { morph } from '@theme/morph';

/* 12/10/2026 — Changer de finition prenait 1 à 3 s sur téléphone : chaque clic retéléchargeait la FICHE ENTIÈRE
   (≈ 380 Ko, 70 Ko compressés) et les photos de l'autre finition, rarement vues, n'étaient pas prêtes sur le CDN
   (1ʳᵉ demande jusqu'à 3 s). Deux changements, sur la fiche produit seulement :
   1. on ne demande que la section principale (`section_id`, ≈ 43 Ko / 7 Ko compressés) — tout ce que le
      changement de variante met à jour (galerie, prix, bouton, stock, barre collante) y vit ; filet : si la
      réponse ne porte pas le sélecteur, on refait la demande sur la page entière ;
   2. une fois la page chargée, on précharge en tâche de fond la réponse de chaque AUTRE valeur (3 au plus) et
      les 2 premières photos de sa galerie : au clic, tout est déjà là.
   Clé = l'URL de la demande ; une réponse préchargée sert une seule fois. */
const prechargements = new Map();
const PRECHARGEMENTS_MAX = 3;

class VariantPicker extends HTMLElement {
  // Contrôleur pour annuler les requêtes en cours
  #abortController;

  connectedCallback() {
    this.addEventListener('change', this.variantChanged.bind(this));
    this.#planifierPrechargement();
  }

  // Identifiant de la section principale de la fiche, ou null hors fiche (carte produit, achat rapide).
  get #sectionId() {
    if (Theme.template.name !== 'product') return null;
    if (this.closest('product-card, quick-add-dialog, dialog')) return null;
    const section = this.closest('.shopify-section');
    return section?.id?.startsWith('shopify-section-') ? section.id.slice('shopify-section-'.length) : null;
  }

  #planifierPrechargement() {
    if (!this.#sectionId || navigator.connection?.saveData) return;
    const lancer = () => (window.requestIdleCallback ?? ((f) => setTimeout(f, 300)))(() => this.#precharger());
    if (document.readyState === 'complete') lancer();
    else window.addEventListener('load', lancer, { once: true });
  }

  #precharger() {
    const choisies = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    const urls = [];
    for (const choisie of choisies) {
      const autres =
        choisie instanceof HTMLOptionElement
          ? Array.from(choisie.closest('select')?.options ?? [])
          : Array.from(choisie.closest('fieldset')?.querySelectorAll('input[type="radio"]') ?? []);
      for (const autre of autres) {
        if (autre === choisie || !autre.dataset.optionValueId) continue;
        const valeurs = choisies.map((c) => (c === choisie ? autre.dataset.optionValueId : c.dataset.optionValueId));
        urls.push(this.buildRequestUrl(valeurs));
      }
    }

    for (const url of urls.slice(0, PRECHARGEMENTS_MAX)) {
      if (prechargements.has(url)) continue;
      const promesse = fetch(url).then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      });
      prechargements.set(url, promesse);
      promesse
        .then((texte) => {
          const html = new DOMParser().parseFromString(texte, 'text/html');
          const photos = Array.from(html.querySelectorAll('product-media-gallery slider-component img')).slice(0, 2);
          for (const photo of photos) {
            const image = new Image();
            const sizes = photo.getAttribute('sizes');
            const srcset = photo.getAttribute('srcset');
            if (sizes) image.sizes = sizes;
            if (srcset) image.srcset = srcset;
            const src = photo.getAttribute('src');
            if (src) image.src = src;
          }
        })
        .catch(() => prechargements.delete(url));
    }
  }

  // Le texte de la réponse : la version préchargée si elle existe (une seule fois), sinon une vraie demande.
  #texteDe(url, signal) {
    const demander = () => fetch(url, { signal }).then((response) => response.text());
    const preparee = prechargements.get(url);
    if (!preparee) return demander();
    prechargements.delete(url);
    return preparee.catch(demander);
  }

  /**
   * Gère l'événement de changement de variante.
   * @param {Event} event - L'événement de changement de variante.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Met à jour l'option sélectionnée dans l'interface
    this.updateSelectedOption(event.target);

    // Déclenche un événement personnalisé pour notifier la sélection
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    // Lance la requête pour mettre à jour la section
    this.fetchUpdatedSection(this.buildRequestUrl());

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    this.updateUrl(event.target);
  }

  // Met à jour l'option sélectionnée dans l'interface.
  updateSelectedOption(target) {
    // Si c'est une chaîne, trouve l'élément correspondant pour récupérer l'ID de la valeur d'option
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    // Met à jour l'état "checked" pour les boutons radio
    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    // Met à jour l'attribut "selected" pour les éléments select
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      // Supprime l'attribut selected de toutes les options
      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      // Ajoute l'attribut selected à la nouvelle option
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  // Construit l'URL de requête pour récupérer les données de variante.
  buildRequestUrl(valeurs = this.selectedOptionsValues) {
    let productUrl = this.dataset.productUrl;

    // On utilise la nouvelle API de Shopify pour récupérer les données de variante avec option_values.
    const params = [];
    params.push(`option_values=${valeurs.join(',')}`);

    // 12/10/2026 — sur la fiche, la section principale suffit (voir l'en-tête du fichier).
    const sectionId = this.#sectionId;
    if (sectionId) params.push(`section_id=${sectionId}`);

    // Si variant-picker est un enfant de quick-add-component ou swatches-variant-picker-component,
    // nous devons ajouter section_id=section-rendering-product-card à l'URL
    // if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
    //   if (productUrl?.includes('?')) {
    //     productUrl = productUrl.split('?')[0];
    //   }
    //   return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    // }
    return `${productUrl}?${params.join('&')}`;
  }

  // Récupère la section mise à jour depuis le serveur.
  fetchUpdatedSection(requestUrl) {
    // Annule la requête fetch précédente si elle est encore en cours
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#texteDe(requestUrl, signal)
      .then((responseText) => {
        // Une réponse préchargée n'écoute pas l'annulation : on l'ignore si un autre clic a suivi.
        if (signal.aborted) return;
        if (this.#appliquerReponse(responseText)) return;

        // Filet : la section seule ne portait pas le sélecteur ⇒ on redemande la page entière.
        if (!requestUrl.includes('section_id=')) return;
        const pageEntiere = requestUrl.replace(/&section_id=[^&]*/, '');
        return fetch(pageEntiere, { signal })
          .then((response) => response.text())
          .then((texte) => {
            if (!signal.aborted) this.#appliquerReponse(texte);
          });
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log("Fetch interrompu par l'utilisateur");
        } else {
          console.error(error);
        }
      });
  }

  // Applique une réponse : met à jour le sélecteur et prévient les autres blocs. Faux si elle est inexploitable.
  #appliquerReponse(responseText) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');

    // Récupère les données JSON de la variante depuis la réponse
    const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
    if (!textContent) return false;

    // Met à jour seulement le variant picker
    this.updateVariantPicker(html);

    // Déclenche un événement pour notifier la mise à jour de la variante
    if (this.selectedOptionId) {
      this.dispatchEvent(
        new VariantUpdatedEvent(JSON.parse(textContent), this.selectedOptionId, {
          html,
          productId: this.dataset.productId ?? '',
        }),
      );
    }
    return true;
  }

  // Re-rend le sélecteur de variante avec les nouvelles données.
  updateVariantPicker(newHtml) {
    // Trouve le nouveau variant picker dans la réponse
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('Aucune nouvelle source de variant picker trouvée');
    }

    // Met à jour le variant picker
    this.innerHTML = newVariantPickerSource.innerHTML;
  }

  // Met à jour l'URL avec l'ID de variante sur les pages produit
  updateUrl(target) {
    // Détermine si nous sommes sur une page produit (pas dans une carte produit ou dialogue)
    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Prépare l'URL pour la mise à jour de l'historique du navigateur
    const url = new URL(window.location.href);

    let variantId;

    // Extrait l'ID de la variante selon le type d'élément (radio ou select)
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      variantId = target.dataset.variantId || null;
    } else if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Met à jour l'URL dans l'historique du navigateur si elle a changé
    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  // Récupère l'option actuellement sélectionnée.
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  // Récupère l'ID de l'option actuellement sélectionnée.
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("Aucun ID de valeur d'option trouvé");
    }

    return optionValueId;
  }

  // Récupère toutes les valeurs d'options actuellement sélectionnées.
  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("Aucun ID de valeur d'option trouvé");

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
